/**
 * Text matching for every search box in the app.
 *
 * THE BUG THIS EXISTS FOR
 * -----------------------
 * Searching "north south" returned nothing, because the position is
 * called "North-South" and the match was a plain lowercased substring
 * test. The user is asked to reproduce punctuation they cannot see and
 * have no reason to guess, and the failure is silent — an empty result
 * list reads as "we don't have that", not "you typed it wrong".
 *
 * It is not one position. Across names and aliases the data carries 443
 * hyphens, 173 pairs of parentheses, 46 slashes, and apostrophes in TWO
 * characters — 14 straight (') and 6 curly (’). Nobody types the curly
 * one deliberately, and on a Mac the keyboard may substitute it without
 * being asked.
 *
 * So both sides are folded to the same shape before comparing:
 * everything that is not a letter or a number is removed, and accents
 * are stripped. "north south", "North-South", "northsouth" and
 * "NORTH—SOUTH" all become `northsouth`.
 *
 * WHY NOT /[^a-z0-9]/
 * -------------------
 * Because it would silently delete the Japanese aliases. The data has
 * kanji and kana throughout — 十字固, 足絡み, 袈裟固 — and stripping to
 * ASCII would reduce those entries to the empty string, making them
 * permanently unmatchable while looking like it worked. `\p{L}` and
 * `\p{N}` are letters and numbers in ANY script, so CJK survives and
 * someone searching in Japanese still finds the technique.
 *
 * The diacritic fold matters for the same reason in the other
 * direction: the Portuguese entries (Em Pé, and the ç in 19 others)
 * should be reachable by typing "em pe" on a keyboard that cannot
 * easily produce é.
 */

/**
 * Fold text to its searchable shape.
 *
 * NFD splits an accented character into base + combining mark, so
 * removing the marks leaves the plain letter. Doing it in this order
 * matters: the strip below would otherwise treat a precomposed é as a
 * single letter and keep the accent.
 */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * Folding is not free — NFD allocates, and a keystroke folds every name,
 * alias and tag on ~1300 nodes. But the SAME strings are folded again on
 * the next keystroke, so a cache turns per-keystroke work into
 * per-string-ever work.
 *
 * Unbounded on purpose: the keys are the app's own finite label
 * vocabulary, not user input. Queries go through `searchMatcher` below,
 * which folds without caching, so a user holding a key down cannot grow
 * this map.
 */
const foldCache = new Map<string, string>();

export function normalizeForSearch(text: string | undefined | null): string {
  if (!text) return '';
  const hit = foldCache.get(text);
  if (hit !== undefined) return hit;
  const folded = fold(text);
  foldCache.set(text, folded);
  return folded;
}

/**
 * Build a predicate for one query, so the query is folded once per
 * search rather than once per candidate.
 *
 * An empty query matches everything, which is what every caller wants
 * for an empty box. Note that a query of pure punctuation — "-", "()" —
 * folds to empty and therefore also matches everything. That is the
 * honest reading: nothing searchable was typed. It also keeps a search
 * for "50/50" from flashing "no results" as the user passes through the
 * slash.
 */
export function searchMatcher(query: string): (text?: string | null) => boolean {
  const q = fold(query);
  if (!q) return () => true;
  return (text) => normalizeForSearch(text).includes(q);
}

/** True when the folded query appears in any of the given fields. */
export function matchesAny(
  query: string,
  fields: Array<string | undefined | null>,
): boolean {
  const match = searchMatcher(query);
  return fields.some(match);
}
